import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { pageView } from "../utils/analytics";

/**
 * 화면 전환.
 *
 * 예전에는 React 상태로만 화면을 바꿨다. 브라우저 히스토리에 아무것도
 * 쌓이지 않아서, iOS 가장자리 스와이프나 안드로이드 뒤로가기 버튼이
 * 앱의 이전 화면이 아니라 사이트 밖으로 나가버렸다. 홈 화면에 추가한
 * 앱에서는 그대로 앱이 닫혔다.
 *
 * 화면을 옮길 때마다 히스토리를 한 칸 쌓고, popstate 로 되돌린다.
 * 앱 안 뒤로가기 버튼도 history.back() 을 거쳐 같은 길로 흐르게 한다 —
 * 두 길이 따로 놀면 히스토리가 어긋나 한참 눌러야 나가지게 된다.
 */
export default function useNavigation() {
  const [screen, setScreen] = useState("loading");
  /** [{ screen, scroll }] — 떠날 때 어디까지 내려가 있었는지 함께 쌓는다. */
  const [history, setHistory] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [bookViewUser, setBookViewUser] = useState(null);
  /** 화면을 열 때 어느 탭부터 볼지. 쓰고 나면 비운다. */
  const [openTab, setOpenTab] = useState(null);
  /** 화면을 열고 짚어줄 항목(지금은 이야기 id). 쓰고 나면 비운다. */
  const [focusId, setFocusId] = useState(null);

  /* 화면이 바뀔 때마다 직접 센다. pushState 로 옮기는 한 장짜리라
     gtag 의 자동 page_view 는 처음 열 때 한 번뿐이다 — 켜두면 홈에서
     책, 행성, 궤적을 아무리 돌아다녀도 방문 1회로 보인다.
     뒤로가기로 돌아온 것도 같은 자리를 지나므로 함께 세어진다. */
  useEffect(() => { pageView(screen); }, [screen]);

  // popstate 는 한 번만 붙으므로 최신 값을 ref 로 본다
  const histRef = useRef(history);
  useEffect(() => { histRef.current = history; }, [history]);

  /** 뒤로 갔을 때 되돌릴 스크롤 위치. 옮기고 나면 비운다. */
  const pendingScroll = useRef(null);

  /* 브라우저의 자동 스크롤 복원을 끈다. 한 장짜리 문서에서 pushState 로
     화면을 바꾸므로, 브라우저가 기억한 위치와 우리가 되돌릴 위치가 서로
     덮어써서 어느 쪽도 맞지 않게 된다. 복원은 아래에서 직접 한다. */
  useEffect(() => {
    try { window.history.scrollRestoration = "manual"; } catch { /* 막힌 환경 */ }
  }, []);

  /* 뒤로 온 화면을 떠날 때 있던 자리로 되돌린다.
     되돌린 화면이 그 자리까지 내려갈 만큼 길어야 하는데, 표지 이미지가
     자리를 잡기 전이면 아직 짧아서 scrollTo 가 중간에 걸린다. 도달할
     때까지 몇 프레임 다시 시도한다. */
  useLayoutEffect(() => {
    const y = pendingScroll.current;
    if (y == null) return;
    pendingScroll.current = null;
    window.scrollTo(0, y);
    if (y === 0) return;

    // 프레임 수로 세면 짧다. 홈은 표지가 다 붙기까지 200ms 를 넘겨서
    // 13프레임(약 216ms)으로는 500px 자리를 못 찾아갔다. 시간으로 준다.
    const deadline = performance.now() + 1500;
    let raf = 0;
    let stopped = false;
    // 그 사이 손으로 스크롤하면 더 끌고 가지 않는다
    const stop = () => { stopped = true; };
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    const tick = () => {
      if (stopped || Math.abs(window.scrollY - y) < 2 || performance.now() > deadline) return;
      window.scrollTo(0, y);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
    };
  }, [screen]);

  const navigate = (to, extra = {}) => {
    /* 스크롤 위치는 setHistory 안에서 읽으면 안 된다. 업데이터는 다음
       렌더에 실행될 수 있고, 그때는 아래 scrollTo(0, 0) 가 이미 지나가서
       늘 0 이 적힌다. React 가 큐가 비었을 때만 즉시 계산해주는 탓에
       어떤 때는 맞고 어떤 때는 0 이 되는 식으로 오락가락했다.
       지금 값을 먼저 집어둔다. */
    const from = screen;
    const y = window.scrollY;
    setHistory((h) => [...h, { screen: from, scroll: y }]);
    if (extra.bookId !== undefined) setSelectedBook(extra.bookId);
    if (extra.userId !== undefined) setSelectedUser(extra.userId);
    if (extra.viewAsUser !== undefined) setBookViewUser(extra.viewAsUser);
    else if (to === "book") setBookViewUser(null);
    setOpenTab(extra.tab ?? null);
    setFocusId(extra.focusId ?? null);
    setScreen(to);
    // 새 화면은 늘 위에서 시작한다
    pendingScroll.current = null;
    window.scrollTo(0, 0);
    try {
      window.history.pushState({ muroo: to }, "");
    } catch {
      /* 히스토리가 막힌 환경에서도 화면 전환 자체는 되어야 한다 */
    }
  };

  /** 실제로 한 칸 되돌린다. 브라우저에서 왔든 앱에서 왔든 여기로 모인다. */
  const applyBack = useCallback(() => {
    const h = histRef.current;
    const prev = h[h.length - 1];
    setHistory(h.slice(0, -1));
    setScreen(prev?.screen || "home");
    // 여기서 바로 옮기면 아직 이전 화면이 그려지지 않아 그만큼 내려갈 수
    // 없다. 그려진 뒤(useLayoutEffect)에 옮긴다.
    pendingScroll.current = prev?.scroll ?? 0;
  }, []);

  /* 홈으로 되돌린다. 되짚어 갈 자리가 사라진 뒤에 쓴다 — 책을 지우고
     navigate("home") 로 가면 히스토리에 그 책이 남아, 뒤로가기가 이제
     없는 책으로 되돌아가 빈 화면이 된다. 쌓아둔 길을 비우고, 지금 칸을
     홈으로 갈아끼운다(pushState 가 아니라 replaceState). */
  const goHome = useCallback(() => {
    setHistory([]);
    setScreen("home");
    pendingScroll.current = null;
    window.scrollTo(0, 0);
    try {
      window.history.replaceState({ muroo: "home" }, "");
    } catch {
      /* 히스토리가 막힌 환경에서도 화면 전환 자체는 되어야 한다 */
    }
  }, []);

  const goBack = useCallback(() => {
    if (histRef.current.length > 0) {
      // 브라우저를 거친다. popstate 가 applyBack 을 부른다.
      window.history.back();
    } else {
      applyBack();
    }
  }, [applyBack]);

  /* 책 화면의 '내 행성으로 돌아가기'. 내 행성은 홈이다.
     처음엔 navigate("user","me") 로 앞으로 쌓았더니, 내 행성의 '돌아가기'
     가 다시 그 책으로 가 책 ↔ 내 행성을 끝없이 돌았다.
     바로 앞이 홈이면 그냥 뒤로 간다 — 홈에서 보던 스크롤 자리가 살아난다.
     아니면 쌓인 길을 비우고 홈으로 간다(goHome). 길이 비어 있으니
     뒤로가기로도 책에 돌아오지 못해 고리가 생기지 않는다. */
  const backToHome = useCallback(() => {
    const h = histRef.current;
    if (h.length > 0 && h[h.length - 1].screen === "home") goBack();
    else goHome();
  }, [goBack, goHome]);

  useEffect(() => {
    const onPop = () => applyBack();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [applyBack]);

  /* 화면 안에서 오른쪽으로 쓸어도 뒤로 간다.
     가장자리가 아닌 곳에서 쓸 때를 위한 것으로, 가장자리 제스처는
     브라우저가 가져가 popstate 로 들어온다. */
  const touchStart = useRef(null);
  const canGoBack = ["book", "user", "friends", "trail", "recent", "incomplete-books"].includes(screen);
  const handleTouchStart = useCallback((e) => {
    /* 손가락으로 그림을 옮기는 자리(전체 화면 우주)에서는 이 제스처를 끈다.
       그림을 오른쪽으로 끄는 것이 뒤로가기로 읽혀 홈으로 튀었다. */
    if (e.target?.closest?.("[data-noswipe]")) { touchStart.current = null; return; }
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);
  const handleTouchEnd = useCallback((e) => {
    if (!touchStart.current || !canGoBack) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    if (dx > 80 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      goBack();
    }
    touchStart.current = null;
  }, [canGoBack, goBack]);

  return {
    screen, setScreen,
    selectedBook, selectedUser, bookViewUser,
    openTab, clearOpenTab: () => setOpenTab(null),
    focusId, clearFocus: () => setFocusId(null),
    navigate, goBack, goHome, backToHome,
    handleTouchStart, handleTouchEnd,
  };
}
